import { Loader2, Zap } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import {
    findMarket,
    useTokenPairs,
    useTokenPairStore,
} from '@/features/tokenPairs';

import { useAgentReactivation } from '../hooks/useAgentReactivation';

/**
 * "Activate HIP-3 markets" banner. Renders (real mode only) when the selected
 * market is a HIP-3 builder market and the account still needs the one-time
 * unified-account activation — either the proactive status read reports it, or
 * an open just failed fast with `HIP3_ACTIVATION_REQUIRED`. Hidden while the
 * agent itself needs reactivation: that banner takes priority because
 * activation requires an approved agent. The button fires the long-running
 * activate POST; the banner self-clears once the status refetch confirms the
 * activation. Never auto-resubmits the trade — the user re-initiates it.
 */
const ActivateHip3Banner: React.FC = () => {
    const {
        isRealMode,
        needsReactivation,
        requiresHip3Activation,
        isActivatingHip3,
        activateHip3,
    } = useAgentReactivation();

    const { selectedCoin, networkId } = useTokenPairStore();
    // Unconditional, like every other consumer of this query. Gating it on
    // `requiresHip3Activation` made the banner's own data availability depend on
    // a condition derived from that same data class — the exact shape of the bug
    // that suppressed this banner on HIP-3 markets. No extra network cost: the
    // picker mounts this query at every breakpoint, so React Query dedupes.
    const { data } = useTokenPairs(networkId);
    const market = findMarket(data?.markets, selectedCoin);
    const selectedIsHip3 =
        market?.requiresHip3Activation === true || market?.dex != null;

    if (
        !isRealMode ||
        !requiresHip3Activation ||
        needsReactivation ||
        !selectedIsHip3
    ) {
        return null;
    }

    return (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2">
            <div className="flex items-start gap-2">
                <Zap
                    size={14}
                    className={
                        isActivatingHip3
                            ? 'mt-0.5 shrink-0 animate-pulse text-amber-400'
                            : 'mt-0.5 shrink-0 text-amber-400'
                    }
                />
                <div>
                    <p className="text-xs font-semibold text-amber-400">
                        Unlock this market
                    </p>
                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                        {isActivatingHip3
                            ? 'Activating HIP-3 markets (up to a minute). Keep this tab open, trade as soon as it finishes.'
                            : 'This market needs a one-time account activation before your first trade.'}
                    </p>
                </div>
            </div>
            <Button
                variant="outline"
                size="sm"
                onClick={activateHip3}
                disabled={isActivatingHip3}
                className="shrink-0 gap-1 border-amber-500/30 text-amber-400 hover:border-amber-500/50 hover:bg-amber-500/10"
            >
                {isActivatingHip3 ? (
                    <>
                        <Loader2 size={14} className="animate-spin" />
                        Activating…
                    </>
                ) : (
                    <>
                        <Zap size={14} />
                        Activate
                    </>
                )}
            </Button>
        </div>
    );
};

ActivateHip3Banner.displayName = 'ActivateHip3Banner';

export default ActivateHip3Banner;
